import { useRef, useState } from "react";
import { api } from "../api";
import { authStore, useAuth } from "./store";
import { PasskeyCancelledError, createPasskey, passkeysSupported } from "./webauthn";
import { CopyButton, Field } from "../components/ui";
import { AuthFrame } from "../routes/auth/LoginPage";

/**
 * Blocking enrollment screen for users with no second factor. It renders
 * instead of the app shell, so no navigation, project data, or org chrome is
 * reachable until a factor is registered.
 */
export function MfaGate() {
  const auth = useAuth();
  const [step, setStep] = useState<"choose" | "totp">("choose");
  const [totp, setTotp] = useState<{ secret: string; otpauthUrl: string; qrSvg: string } | null>(null);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const starting = useRef(false);

  // Re-read /v1/me and let it release the gate. Clearing the pending step here
  // instead would open the app for a paint if the reload failed.
  const finish = async () => {
    await authStore.load();
  };

  const startTotp = async () => {
    if (starting.current) return;
    starting.current = true;
    setError(null);
    setBusy(true);
    try {
      setTotp(await api.me.totpStart());
      setStep("totp");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      starting.current = false;
      setBusy(false);
    }
  };

  const confirmTotp = async () => {
    setError(null);
    setBusy(true);
    try {
      const r = await api.me.totpConfirm(code.replace(/\s+/g, ""));
      setRecovery(r.recoveryCodes);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const addPasskey = async () => {
    setError(null);
    setNote(null);
    setBusy(true);
    try {
      const opts = await api.me.passkeyRegisterOptions();
      const { challengeId, credential } = await createPasskey(opts);
      await api.me.passkeyRegisterVerify(challengeId, credential, "Passkey");
      await finish();
    } catch (e) {
      if (e instanceof PasskeyCancelledError) setNote("No passkey was created. Try again, or use an authenticator app instead.");
      else setError((e as Error).message);
      setBusy(false);
    }
  };

  const signOut = async () => {
    await authStore.logout();
  };

  if (recovery) {
    return (
      <AuthFrame title="Save your recovery codes" lead="Each code works once. They are the only way back in if you lose your authenticator.">
        <div className="code-list">
          {recovery.map((c) => (
            <span key={c}>{c}</span>
          ))}
        </div>
        <div style={{ margin: "12px 0" }}>
          <CopyButton value={recovery.join("\n")} label="Copy all" />
        </div>
        <button className="btn primary block" onClick={() => void finish()}>
          I've saved them — continue
        </button>
      </AuthFrame>
    );
  }

  if (step === "totp" && totp) {
    return (
      <AuthFrame title="Set up your authenticator app" lead="Scan this with 1Password, Google Authenticator, Authy, or any TOTP app.">
        {error && (
          <div className="error-box" role="alert">
            {error}
          </div>
        )}
        <div className="qr" dangerouslySetInnerHTML={{ __html: totp.qrSvg }} />
        <details style={{ margin: "12px 0" }}>
          <summary className="muted small">Can't scan? Enter the key manually</summary>
          <div className="mono-box" style={{ marginTop: 6 }}>
            {totp.secret} <CopyButton value={totp.secret} />
          </div>
        </details>
        <Field label="6-digit code">
          {(p) => <input {...p} className="input" inputMode="numeric" autoComplete="off" value={code} onChange={(e) => setCode(e.target.value)} autoFocus />}
        </Field>
        <button className="btn primary block" onClick={() => void confirmTotp()} disabled={busy || code.replace(/\s/g, "").length < 6}>
          Confirm and continue
        </button>
        <div className="auth-links">
          <button className="btn ghost" onClick={() => setStep("choose")}>
            Back
          </button>
        </div>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame title="Set up two-factor authentication" lead={`${auth.me?.user.email ?? "Your account"} needs a second factor before you can use Backstory.`}>
      {error && (
        <div className="error-box" role="alert">
          {error}
        </div>
      )}
      {note && !error && (
        <p className="auth-note" role="status">
          {note}
        </p>
      )}
      {passkeysSupported() ? (
        <button className="btn primary block" onClick={() => void addPasskey()} disabled={busy}>
          Add a passkey
        </button>
      ) : (
        <p className="muted small">Passkeys are not supported in this browser.</p>
      )}
      <div className="divider">or</div>
      <button className="btn block" onClick={() => void startTotp()} disabled={busy}>
        Use an authenticator app
      </button>
      <div className="auth-links">
        <button className="btn ghost" onClick={() => void signOut()}>
          Sign out
        </button>
      </div>
    </AuthFrame>
  );
}
